import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, Heart } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useStore, money } from "@/context/StoreContext";
import { Progress } from "@/components/ui/progress";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal, count } = useCart();
  const { toggleWishlist } = useAuth();
  const { settings } = useStore();
  const nav = useNavigate();
  const threshold = settings?.free_shipping_threshold || 999;
  const remaining = Math.max(threshold - subtotal, 0);
  const pct = Math.min((subtotal / threshold) * 100, 100);

  const go = () => { setOpen(false); nav("/cart"); };
  const checkout = () => { setOpen(false); nav("/checkout"); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-white p-0 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-sharoa-line">
          <h2 className="font-serif text-xl">Your Bag ({count})</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close"><X size={20} /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-4">
            <p className="font-serif text-2xl text-sharoa-charcoal">Your bag is empty</p>
            <p className="text-sm text-sharoa-muted">Discover pieces made for your everyday ♡</p>
            <Link to="/shop" onClick={() => setOpen(false)} className="bg-sharoa-rose text-white text-xs uppercase tracking-widest px-6 py-3">Shop Now</Link>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 bg-sharoa-pale">
              {remaining > 0 ? (
                <p className="text-xs text-sharoa-charcoal mb-2">You are <span className="font-mono text-sharoa-rose">{money(remaining)}</span> away from FREE SHIPPING ♡</p>
              ) : (
                <p className="text-xs text-sharoa-rose mb-2 font-medium">You've unlocked FREE SHIPPING ♡</p>
              )}
              <Progress value={pct} className="h-1.5" />
            </div>
            <div className="flex-1 overflow-y-auto px-5 divide-y divide-sharoa-line">
              {items.map((it) => (
                <div key={it.sku} data-testid={`cart-item-${it.sku}`} className="flex gap-3 py-4">
                  <img src={it.image} alt={it.name} className="w-20 h-24 object-cover bg-sharoa-ivory" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-serif text-sm text-sharoa-charcoal">{it.name}</h4>
                      <button onClick={() => removeItem(it.sku)} data-testid={`cart-remove-${it.sku}`}><Trash2 size={15} className="text-sharoa-muted hover:text-sharoa-rose" /></button>
                    </div>
                    <p className="text-xs text-sharoa-muted mt-0.5">{it.size} · {it.color}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-sharoa-line">
                        <button onClick={() => updateQty(it.sku, it.quantity - 1)} className="px-2 py-1"><Minus size={13} /></button>
                        <span className="px-2 text-sm font-mono">{it.quantity}</span>
                        <button onClick={() => updateQty(it.sku, it.quantity + 1)} className="px-2 py-1"><Plus size={13} /></button>
                      </div>
                      <span className="font-mono text-sm">{money(it.price * it.quantity)}</span>
                    </div>
                    <button onClick={() => { toggleWishlist(it.product_id); removeItem(it.sku); }}
                      className="text-[11px] text-sharoa-muted hover:text-sharoa-rose mt-2 flex items-center gap-1"><Heart size={11} /> Move to Wishlist</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-sharoa-line p-5 space-y-3">
              <div className="flex justify-between font-serif text-lg"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
              <button onClick={checkout} data-testid="cart-checkout-btn" className="w-full bg-sharoa-rose hover:bg-sharoa-rosedark text-white text-xs uppercase tracking-[0.18em] py-4 transition-colors">Proceed to Checkout</button>
              <button onClick={go} className="w-full text-xs uppercase tracking-widest text-sharoa-charcoal py-2 sh-underline mx-auto block">View Bag</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
